import { handleEndDate } from '@/resources/utils'
import { getQueryVariables } from '@/services/orders/history'
import type {
  HistoryFilterAction,
  HistoryFiltersStore,
} from '@/hooks/orderHistory/types'

const {
  period: { start, end },
  pagination: { page, pageSize },
} = getQueryVariables()

export const filtersDefault: HistoryFiltersStore = {
  search: undefined,
  orderStatus: [],
  ratings: [],
  courierEmail: '',
  paymentMethods: [],
  deliveryTypes: [],
  period: { start: start.toISOString(), end: end.toISOString() },
  page,
  pageSize,
  hasMoreOrders: true,
}

const setSearch = (
  state: HistoryFiltersStore,
  searchValue: HistoryFiltersStore['search']
) => {
  const hasValue = (searchValue?.value?.length ?? 0) > 0
  return { ...state, search: hasValue ? searchValue : undefined }
}

const setFilter = <Key extends keyof HistoryFiltersStore>(
  state: HistoryFiltersStore,
  key: Key,
  value: HistoryFiltersStore[Key]
) => ({ ...state, [key]: value })

const clearFilters = () => {
  return filtersDefault
}

const setPeriod = (state: HistoryFiltersStore, value: [Date, Date]) => {
  const endDate = value[1]
  handleEndDate(endDate)

  return {
    ...state,
    period: {
      start: value[0].toISOString(),
      end: endDate.toISOString(),
    },
  }
}

const updateMultipleFilters = (
  state: HistoryFiltersStore,
  value: Partial<HistoryFiltersStore>
) => {
  return {
    ...state,
    ...value,
  }
}

const filtersReducer = (
  state: HistoryFiltersStore,
  action: HistoryFilterAction
): HistoryFiltersStore => {
  const nextState = {
    ...state,
    page: action.resetPage ? page : state.page,
  }

  switch (action.type) {
    case 'SEARCH':
      return setSearch(nextState, action.value)
    case 'COURIER_EMAIL':
      return setFilter(nextState, 'courierEmail', action.value)
    case 'STATUS':
      return setFilter(nextState, 'orderStatus', action.value)
    case 'RATINGS':
      return setFilter(nextState, 'ratings', action.value)
    case 'PAYMENT_METHODS':
      return setFilter(nextState, 'paymentMethods', action.value)
    case 'DELIVERY_TYPES':
      return setFilter(nextState, 'deliveryTypes', action.value)
    case 'CLEAR_FILTERS':
      return clearFilters()
    case 'PERIOD':
      return setPeriod(nextState, action.value)
    case 'PAGE':
      return setFilter(nextState, 'page', action.value)
    case 'PAGE_SIZE':
      return setFilter(nextState, 'pageSize', action.value)
    case 'HAS_MORE_ORDERS':
      return setFilter(nextState, 'hasMoreOrders', action.value)
    case 'UPDATE_MULTIPLE':
      return updateMultipleFilters(nextState, action.value)
  }
}

export default filtersReducer
